import React, {memo, useCallback, useMemo, useEffect} from 'react';
import {useNumpadExtended} from '../../hooks/numpad-extended';
import startCallIcon from './../../assets/images/icons/call-screen/call_start.png';
import leftArrowIcon from './../../assets/images/icons/media/left.png';
import {Row} from '../../components/Row';
import {Column} from '../../components/Column';
import {ConciergeModel} from '../../types/concierge';
import {useRPC} from '../../hooks/rpc';
import {onClickHandler} from '../../utils/onclick';
import {useDispatch, useSelector} from 'react-redux';
import {Container} from '../../components/Container';
import './index.scss';
import {ConciergeTypeModel} from '../../types/concierge-type';
import {useSet} from '../../hooks/set';
import {Image} from '../../components/Image';
import {useLocalization} from '../../hooks/localization';
import {useHistory, useLocation} from 'react-router-dom';
import {setIntercomStatusAction} from "../../store/control/actions";
import {useUpdate} from "../../hooks/update-effect";
import {useMessage} from "../../hooks/message";
import {backButtonToggle} from "../../store/ui/actions";

const ConciergeItemComponent = memo(function ConciergeItemComponent(props: { concierge: ConciergeModel, conciergeType: ConciergeTypeModel }) {
    const [rpc] = useRPC();
    const {concierge, conciergeType} = props;

    const onClick = useCallback(() => {
        rpc.callIntercom(concierge.info.sip);
    }, [rpc, concierge?.info?.sip]);

    return (
        <div className="concierge-item-container" {...onClickHandler(onClick)}>
            <div className="img-container">
                <Image src={conciergeType.icon} alt={concierge.name} disableErrorHandling={true}/>
            </div>
            <span>{concierge.name}</span>
        </div>
    );
});

const specialKeys = {
    '*': <img src={startCallIcon} alt="call"/>,
    '#': <img src={leftArrowIcon} alt="delete"/>,
};

export const MeetingScreen = memo(function MeetingScreen() {
    const {t} = useLocalization();
    const history = useHistory();
    const location = useLocation();
    const [rpc] = useRPC();
    const dispatch = useDispatch();
    const conciergeList = useSelector(state => state.control.conciergeList);
    const conciergeTypeSet = useSet(useSelector(state => state.control.conciergeTypeList));
    const status = useSelector(state=>state.control.intercomState.status);
    const {failure} = useMessage();

    const filteredConciergeList = useMemo(() => {
        return conciergeList?.filter(item => item.info?.sip) || [];
    }, [conciergeList]);

    const onKeyPressed = useCallback((key: string, input: string) => {
        if (key === '*') {
            rpc.callIntercom(input)
                .then(() => {
                    dispatch(setIntercomStatusAction('incoming', false, input, input));
                });
        }
    }, [rpc]);

    const [renderNumpad] = useNumpadExtended(false, specialKeys, onKeyPressed);


    useUpdate(()=>{
        if(status === 'ended'){
            failure(t("error_call"), true)
        }
    }, [status]);
    
     useEffect(() => {
       if (location.pathname === "/meeting") {
         dispatch(backButtonToggle({ is_open: true, to: "/concierge" }));
       }
     }, [dispatch, location]);

    return (
        <Container className="meeting-screen">
            <div className="darker">
                <div className="content-divider">
                    <div className="information-container">
                        <span>{t('enter_flat_no_wish_to_call')}</span>
                    </div>
                    <div className="concierge-list-container">
                        <Row>
                            {
                                filteredConciergeList.map(item => (
                                    <Column min={4} max={4} key={`concierge_item_${item.id}`}>
                                        <ConciergeItemComponent concierge={item}
                                                                conciergeType={conciergeTypeSet[item.type_id]}/>
                                    </Column>
                                ))
                            }
                        </Row>
                    </div>
                </div>
                <div className="content-divider">
                    {
                        renderNumpad()
                    }
                </div>
            </div>
        </Container>
    );
});
